import {observer} from 'mobx-react';
import React, {useEffect, useMemo} from 'react';

import {FunnelLogContextProvider} from '@udemy/funnel-tracking';
import {DiscoveryUnitsContainerStore} from '@udemy/react-discovery-units';
import {useUDData} from '@udemy/ud-data';

import {TopicProps} from '../topic.react-component';
import {getPageObjectIdFromTopic, getPageType} from '../utils';
import {isPracticeExamDiscoveryUnit} from './topic-discovery-units';
import {TopicDiscoveryUnitsContainerWrapper} from './topic-discovery-units-wrapper.react-component';

const PRACTICE_EXAM_DISCOVERY_FETCH_OPTIONS = {pageSize: 50};

export interface CertificationPracticeExamSectionProps
    extends Pick<
        TopicProps,
        'topic' | 'isConsumerSubsSubscriber' | 'isSubsLoading' | 'enrollmentStat'
    > {
    isMobile: boolean;
}

/**
 * Renders the practice-exam carousel the same way the non-experiment topic page does:
 * via DiscoveryUnitsContainer, which provides `discoveryUnitsStore` and the `unit` prop.
 */
export const CertificationPracticeExamSection = observer(
    (props: CertificationPracticeExamSectionProps) => {
        const {topic, enrollmentStat, isMobile, isConsumerSubsSubscriber, isSubsLoading} = props;
        const udData = useUDData();
        const isDiscoveryProviderReady = !isSubsLoading && !udData.isGlobalMeContextLoading;
        const pageType = getPageType(udData, isConsumerSubsSubscriber);
        const pageObjectId = getPageObjectIdFromTopic(topic);

        const discoveryStore = useMemo(
            () =>
                isDiscoveryProviderReady
                    ? new DiscoveryUnitsContainerStore({pageType, pageObjectId})
                    : null,
            [isDiscoveryProviderReady, pageType, pageObjectId],
        );

        useEffect(
            function fetchPracticeExamDiscoveryUnits() {
                if (!discoveryStore) {
                    return;
                }

                if (!discoveryStore.loading && discoveryStore.firstLoad && !discoveryStore.error) {
                    discoveryStore.fetchUnits(PRACTICE_EXAM_DISCOVERY_FETCH_OPTIONS);
                }
            },
            [discoveryStore],
        );

        if (!discoveryStore || discoveryStore.firstLoad) {
            return null;
        }

        const hasPracticeExamUnit = discoveryStore.units.some(isPracticeExamDiscoveryUnit);
        if (!hasPracticeExamUnit) {
            return null;
        }

        return (
            <div className="component-margin ud-container">
                <FunnelLogContextProvider pageType="topic">
                    <TopicDiscoveryUnitsContainerWrapper
                        topic={topic}
                        enrollmentStat={enrollmentStat}
                        isMobile={isMobile}
                        store={discoveryStore}
                        pageType={pageType}
                        discoveryUnitFilter={isPracticeExamDiscoveryUnit}
                        fetchOptions={PRACTICE_EXAM_DISCOVERY_FETCH_OPTIONS}
                        disableInfiniteScroll
                    />
                </FunnelLogContextProvider>
            </div>
        );
    },
);
